import { V4 } from '@moleculeone/utils';

export function getBond(mol, start, end) {
  for (const bond of Object.values(mol.bonds)) {
    const match1 = start === bond.start && end === bond.end;
    const match2 = start === bond.end && end === bond.start;
    if (match1 || match2) {
      return bond;
    }
  }
  return null;
}

export function neighborsList(mol, atomIdx) {
  const result = [];
  for (const bond of Object.values(mol.bonds)) {
    if (bond.start === atomIdx) {
      result.push([bond, mol.atoms[bond.end]]);
    } else if (bond.end === atomIdx) {
      result.push([bond, mol.atoms[bond.start]]);
    }
  }
  return result;
}

export function atomToNeighborsPairs(mol) {
  const result = {};
  for (const k of Object.keys(mol.atoms)) {
    result[k] = [];
  }

  for (const bond of Object.values(mol.bonds)) {
    result[bond.start].push([bond, mol.atoms[bond.end]]);
    result[bond.end].push([bond, mol.atoms[bond.start]]);
  }
  return result;
}

export function atomToNeighborsIndices(mol) {
  const result = {};
  for (const atom of Object.keys(mol.atoms)) {
    result[atom] = [];
  }
  for (const bond of Object.values(mol.bonds)) {
    result[bond.start].push(bond.end);
    result[bond.end].push(bond.start);
  }
  return result;
}

export function getLoopSelectedAtoms(mol, loopPoints) {
  if (loopPoints.length < 3) {
    return [];
  }
  return Object.entries(mol.positions)
    .filter(([key, pos]) => V4.inConcave(pos, loopPoints))
    .map(([key]) => key);
}

export function getExplicitValences(mol) {
  const explicitValences = {};
  Object.keys(mol.atoms).forEach(key => (explicitValences[key] = 0));
  Object.values(mol.bonds).forEach(bond => {
    explicitValences[bond.start] += bond.order;
    explicitValences[bond.end] += bond.order;
  });
  return explicitValences;
}

// 'start' is at the beginning and 'end' at the end of the result.
export function* edgeCycleSearch(start, end, keyToNeighbors) {
  let lastVisited = [];
  const parentsMap = {};
  parentsMap[start] = end;
  for (const neighbor of keyToNeighbors(start)) {
    if (neighbor !== end) {
      parentsMap[neighbor] = start;
      lastVisited.push(neighbor);
    }
  }

  while (lastVisited.length !== 0) {
    const visited = [];
    for (let current of lastVisited) {
      for (const next of keyToNeighbors(current)) {
        if (parentsMap[next] != null) {
          continue;
        }

        if (next === end) {
          const cycle = [next];
          while (current !== next) {
            cycle.push(current);
            current = parentsMap[current];
          }
          cycle.reverse();
          return cycle;
        }

        parentsMap[next] = current;
        visited.push(next);
      }
    }
    lastVisited = visited;
    yield;
  }
}

// 'start' is the first element of the result
export function* vertexCycleSearch(start, keyToNeighbors) {
  let lastVisited = [];
  const parentsMap = {};
  for (const neighbor of keyToNeighbors(start)) {
    parentsMap[neighbor] = start;
    lastVisited.push([start, neighbor]);
  }

  if (lastVisited.length < 2) {
    // there's no way to form a cycle, early return.
    return;
  }

  while (lastVisited.length !== 0) {
    const visited = [];
    for (let [last, current] of lastVisited) {
      for (let next of keyToNeighbors(current)) {
        if (last === next) {
          continue;
        }

        if (parentsMap[next] != null) {
          const cycle = [];
          while (current != null) {
            cycle.unshift(current);
            current = parentsMap[current];
          }
          while (next !== cycle[0]) {
            cycle.push(next);
            next = parentsMap[next];
          }
          return cycle;
        }

        parentsMap[next] = current;
        visited.push([current, next]);
      }
    }
    lastVisited = visited;
    yield;
  }
}

export function getSmallestCycles(mol) {
  const searches = {};
  const bondToCycle = {};
  const distinctCycles = [];
  const nborsIndices = atomToNeighborsIndices(mol);
  for (const [bondIdx, bond] of Object.entries(mol.bonds)) {
    searches[bondIdx] = edgeCycleSearch(bond.start, bond.end, atomIdx => nborsIndices[atomIdx]);
  }

  while (Object.keys(searches).length !== 0) {
    for (const [bondIdx, search] of Object.entries(searches)) {
      if (bondToCycle[bondIdx] != null) {
        delete searches[bondIdx];
        continue;
      }

      const { value, done } = search.next();
      const newCycle = value;
      if (newCycle) {
        distinctCycles.push(newCycle);
        bondToCycle[bondIdx] = newCycle;
        for (let i = 1; i < newCycle.length; i++) {
          const bondKey = getBond(mol, newCycle[i - 1], newCycle[i]).idx;
          if (bondToCycle[bondKey] == null) {
            bondToCycle[bondKey] = newCycle;
          }
        }
      } else if (done) {
        delete searches[bondIdx];
      }
    }
  }

  return { bondToCycle, distinctCycles };
}

export function areBondsSeparate(bond1, bond2) {
  return (
    bond1.start !== bond2.start && bond1.start !== bond2.end && bond1.end !== bond2.start && bond1.end !== bond2.end
  );
}
